<template>
    <div class="batch-register">
        <div class="tool-tip">本页面支持单次200个账号批量注册，在注册前请自行确认账号是否被注册，如果被注册请自行更换用户名前缀。</div>
        <div class="content-container">
            <div class="register-form">
                <el-form 
                    :model="registerForm" 
                    :rules="registerRules" 
                    ref="registerFormRef"
                    label-width="120px"
                >
                    <el-form-item label="用户名前缀" prop="username">
                        <el-input 
                            v-model="registerForm.username" 
                            placeholder="请输入用户名前缀"
                            style="width: 220px"
                        />
                    </el-form-item>
                    <el-form-item label="昵称前缀" prop="nickname">
                        <el-input 
                            v-model="registerForm.nickname" 
                            placeholder="请输入昵称前缀"
                            style="width: 220px"
                        />
                    </el-form-item>
                    <el-form-item label="密码" prop="password">
                        <el-input 
                            v-model="registerForm.password" 
                            placeholder="请输入密码"
                            type="password"
                            style="width: 220px"
                            show-password
                        />
                    </el-form-item>
                    <el-form-item label="邮箱" prop="email">
                        <el-input 
                            v-model="registerForm.email" 
                            placeholder="请输入邮箱"
                            style="width: 220px"
                        />
                    </el-form-item>
                    <el-form-item label="真实姓名" prop="realName">
                        <el-input 
                            v-model="registerForm.realName" 
                            placeholder="请输入真实姓名"
                            style="width: 220px"
                        />
                    </el-form-item>
                    <el-form-item label="身份证号" prop="idCard">
                        <el-input 
                            v-model="registerForm.idCard" 
                            placeholder="请输入身份证号"
                            style="width: 220px"
                        />
                    </el-form-item>
                    <el-form-item label="起始编号" prop="startIndex">
                        <el-input-number 
                            v-model="registerForm.startIndex" 
                            :min="1"
                            controls-position="right"
                            style="width: 220px"
                        />
                    </el-form-item>
                    <el-form-item label="结束编号" prop="endIndex">
                        <el-input-number 
                            v-model="registerForm.endIndex" 
                            :min="1"
                            controls-position="right"
                            style="width: 220px"
                        />
                    </el-form-item>
                    <el-form-item>
                        <el-button 
                            type="primary" 
                            @click="startRegister" 
                            :loading="isRegistering"
                            style="width: 220px"
                        >
                            开始注册
                        </el-button>
                    </el-form-item>
                </el-form>
            </div>

            <div v-if="registrationResults" class="registration-results">
                <div class="results-header">
                    <h3>注册结果</h3>
                    <el-button type="primary" link @click="copyResults">
                        <el-icon><DocumentCopy /></el-icon>
                        复制结果
                    </el-button>
                </div>
                <div class="results-content">
                    <div class="base-info">
                        <h4>注册基础信息：</h4>
                        <p>邮箱：{{ registerForm.email }}</p>
                        <p>身份证：{{ registerForm.idCard }}</p>
                        <p>真实姓名：{{ registerForm.realName }}</p>
                    </div>
                    <div class="accounts-info">
                        <h4>账号详情：</h4>
                        <div class="account-list">
                            <p v-for="(account, index) in successAccounts" :key="index">
                                {{ account.username }} / {{ account.password }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog
            v-model="progressVisible"
            title="注册进度"
            :close-on-click-modal="false"
            :close-on-press-escape="false"
            :show-close="progressStatus === 'exception'"
            width="400px"
        >
            <div class="progress-info">
                <el-progress 
                    :percentage="progressPercentage" 
                    :status="progressStatus"
                />
                <p>{{ progressMessage }}</p>
                <p v-if="currentStats">
                    成功：{{ currentStats.successCount }} / 
                    失败：{{ currentStats.failureCount }} / 
                    总数：{{ currentStats.total }}
                </p>
            </div>
            <template #footer v-if="progressStatus === 'exception'">
                <el-button @click="progressVisible = false">关闭</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { DocumentCopy } from '@element-plus/icons-vue'
import axios from 'axios'

export default {
    name: 'BatchRegister',
    components: {
        DocumentCopy
    },
    setup() {
        const registerFormRef = ref(null)
        const registerForm = reactive({
            username: '',
            nickname: '',
            password: '',
            email: '',
            realName: '',
            idCard: '',
            startIndex: 1,
            endIndex: 1
        })

        const registerRules = {
            username: [
                { required: true, message: '请输入用户名前缀', trigger: 'blur' },
                { pattern: /^[a-zA-Z0-9_]{1,13}$/, message: '用户名前缀只能包含英文、数字、下划线，最长13个字符（预留3位编号）', trigger: 'blur' }
            ],
            nickname: [
                { required: true, message: '请输入昵称前缀', trigger: 'blur' },
                { 
                    validator: (rule, value, callback) => {
                        if (!value) {
                            callback();
                            return;
                        }
                        // 计算字符长度（汉字占2个字符）
                        const len = value.split('').reduce((total, char) => {
                            return total + (/[\u4e00-\u9fa5]/.test(char) ? 2 : 1);
                        }, 0);
                        if (len > 13) {
                            callback(new Error('昵称前缀最长13个字符（汉字占2个字符，预留3位编号）'));
                            return;
                        }
                        callback();
                    },
                    trigger: 'blur'
                }
            ],
            password: [
                { required: true, message: '请输入密码', trigger: 'blur' },
                { min: 6, message: '密码长度不能小于6位', trigger: 'blur' }
            ],
            email: [
                { required: true, message: '请输入邮箱', trigger: 'blur' },
                { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
            ],
            realName: [
                { required: true, message: '请输入真实姓名', trigger: 'blur' }
            ],
            idCard: [
                { required: true, message: '请输入身份证号', trigger: 'blur' },
                { pattern: /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/, message: '请输入正确的身份证号格式', trigger: 'blur' }
            ],
            startIndex: [
                { required: true, message: '请输入起始编号', trigger: 'blur' },
                { type: 'number', min: 1, message: '起始编号必须大于0', trigger: 'blur' }
            ],
            endIndex: [
                { required: true, message: '请输入结束编号', trigger: 'blur' },
                { type: 'number', min: 1, message: '结束编号必须大于0', trigger: 'blur' }
            ]
        }

        const isRegistering = ref(false)
        const progressVisible = ref(false)
        const progressPercentage = ref(0)
        const progressStatus = ref('')
        const progressMessage = ref('')
        const currentStats = ref(null)
        const registrationResults = ref(null)
        const successAccounts = ref([])

        const validateForm = async () => {
            try {
                await registerFormRef.value.validate();
                
                if (registerForm.endIndex < registerForm.startIndex) {
                    throw new Error('结束编号必须大于起始编号');
                }
                //限制注册数量
                if (registerForm.endIndex - registerForm.startIndex > 200) {
                    throw new Error('单次注册数量不能超过200个');
                }

                // 验证用户名长度
                const maxNumberLength = String(registerForm.endIndex).length;
                const usernamePrefix = registerForm.username;
                if (usernamePrefix.length + maxNumberLength > 16) {
                    throw new Error(`用户名前缀过长，当前编号最大${maxNumberLength}位，用户名前缀最多允许${16 - maxNumberLength}个字符`);
                }

                // 验证昵称长度
                const nicknamePrefix = registerForm.nickname;
                const nicknamePrefixLength = nicknamePrefix.split('').reduce((total, char) => {
                    return total + (/[\u4e00-\u9fa5]/.test(char) ? 2 : 1);
                }, 0);
                if (nicknamePrefixLength + maxNumberLength > 16) {
                    throw new Error(`昵称前缀过长，当前编号最大${maxNumberLength}位，昵称前缀最多允许${16 - maxNumberLength}个字符（汉字占2个字符）`);
                }

                // 验证用户名格式
                if (!/^[a-zA-Z0-9_]+$/.test(usernamePrefix)) {
                    throw new Error('用户名前缀只能包含英文、数字、下划线');
                }

                return true;
            } catch (error) {
                if (error instanceof Error) {
                    ElMessage.error(error.message);
                }
                return false;
            }
        }

        const startRegister = async () => {
            if (!await validateForm()) return

            isRegistering.value = true
            progressVisible.value = true
            progressPercentage.value = 0
            progressStatus.value = ''
            progressMessage.value = '准备开始注册...'
            currentStats.value = null
            successAccounts.value = []
            registrationResults.value = null

            // 创建自定义的axios实例
            const axiosInstance = axios.create({
                baseURL: 'https://apileeuu.adil.com.cn',
                timeout: 30 * 60 * 1000, // 30分钟超时
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
                withCredentials: false, // 改为 false，因为不需要发送 cookies
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream'
                }
            });

            // 添加响应拦截器处理错误
            axiosInstance.interceptors.response.use(
                response => response,
                error => {
                    if (error.response && error.response.status === 524) {
                        // 处理524超时错误，尝试重新发送请求
                        const retryConfig = {
                            ...error.config,
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'text/event-stream'
                            }
                        };
                        return axiosInstance(retryConfig);
                    }
                    return Promise.reject(error);
                }
            );

            try {
                const response = await axiosInstance.post('/leeuu/batch-register', {
                    username: registerForm.username,
                    nickname: registerForm.nickname,
                    password: registerForm.password,
                    email: registerForm.email,
                    realName: registerForm.realName,
                    idCard: registerForm.idCard,
                    startIndex: registerForm.startIndex,
                    endIndex: registerForm.endIndex,
                    batchSize: 100,
                    concurrentLimit: 5
                });

                // 处理响应数据
                const lines = response.data.split('\n');
                for (const line of lines) {
                    if (!line.trim()) continue;
                    try {
                        const data = JSON.parse(line);
                        handleStreamResponse(data);
                    } catch (e) {
                        // 忽略不完整的JSON
                        if (line !== lines[lines.length - 1]) {
                            console.warn('解析响应数据失败:', e, line);
                        }
                    }
                }

            } catch (error) {
                console.error('注册请求失败:', error);
                
                let errorMessage = '未知错误';
                if (error.response) {
                    // 服务器返回错误状态码
                    errorMessage = `服务器返回 ${error.response.status} 错误`;
                } else if (error.code === 'ECONNABORTED') {
                    // 请求超时
                    errorMessage = '请求超时，请稍后重试';
                } else if (error.message === 'Network Error') {
                    // 网络错误（包括CORS错误）
                    errorMessage = '网络错误，请检查网络连接或联系管理员处理跨域问题';
                } else {
                    // 其他错误
                    errorMessage = error.message;
                }

                progressStatus.value = 'exception';
                progressMessage.value = '注册失败：' + errorMessage;
                ElMessage.error('注册失败：' + errorMessage);
            } finally {
                isRegistering.value = false;
            }
        }

        const handleStreamResponse = (data) => {
            if (!data || typeof data !== 'object') return;
            console.log('收到响应数据:', data);

            switch (data.type) {
                case 'init':
                    progressMessage.value = data.message;
                    currentStats.value = {
                        total: data.total,
                        successCount: 0,
                        failureCount: 0
                    };
                    break;

                case 'progress':
                    // 计算总体进度百分比
                    progressPercentage.value = Math.floor((data.currentBatch / data.totalBatches) * 100);
                    
                    // 更新统计信息
                    currentStats.value = {
                        total: data.total || data.processedCount,
                        successCount: data.successCount,
                        failureCount: data.failureCount
                    };
                    
                    // 更新进度消息
                    progressMessage.value = `正在处理第 ${data.currentBatch}/${data.totalBatches} 批`;
                    
                    // 添加成功注册的账号
                    if (data.accounts && Array.isArray(data.accounts)) {
                        data.accounts.forEach(account => {
                            if (account.status === 'success') {
                                successAccounts.value.push({
                                    username: account.username,
                                    password: account.password
                                });
                            }
                        });
                    }
                    break;

                case 'complete':
                    progressPercentage.value = 100;
                    progressStatus.value = 'success';
                    progressMessage.value = '注册完成！';
                    
                    // 更新最终统计信息
                    currentStats.value = {
                        total: data.totalRegistered,
                        successCount: data.successCount,
                        failureCount: data.failureCount
                    };
                    
                    registrationResults.value = {
                        success: true,
                        summary: data.summary
                    };
                    
                    // 延迟关闭进度对话框
                    setTimeout(() => {
                        progressVisible.value = false;
                    }, 1500);
                    break;

                case 'error':
                    progressStatus.value = 'exception';
                    progressMessage.value = '注册失败：' + (data.message || '未知错误');
                    ElMessage.error(data.message || '注册失败');
                    break;

                default:
                    console.warn('未知的消息类型:', data);
            }
        }

        const copyResults = () => {
            if (!registrationResults.value) return

            const baseInfo = `注册基础信息：
邮箱：${registerForm.email}
身份证：${registerForm.idCard}
真实姓名：${registerForm.realName}

账号详情：
${successAccounts.value.map(account => `${account.username} / ${account.password}`).join('\n')}`

            navigator.clipboard.writeText(baseInfo)
                .then(() => ElMessage.success('复制成功'))
                .catch(() => ElMessage.error('复制失败，请手动复制'))
        }

        return {
            registerFormRef,
            registerForm,
            registerRules,
            isRegistering,
            progressVisible,
            progressPercentage,
            progressStatus,
            progressMessage,
            currentStats,
            registrationResults,
            successAccounts,
            startRegister,
            copyResults
        }
    }
}
</script>

<style scoped>
.batch-register {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.tool-tip {
    font-size: 14px;
    color: #666;
    margin: 0;
    padding: 12px;
    background-color: #f5f7fa;
    border-radius: 4px;
    border-left: 4px solid #409eff;
}

.content-container {
    display: flex;
    flex-direction: row;
    gap: 24px;
    width: 100%;
}

.register-form {
    flex: 0 0 auto;
    min-width: 360px;
}

.registration-results {
    flex: 1;
    padding: 20px;
    background: var(--el-bg-color-page);
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    min-width: 300px;
}

.results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.results-header h3 {
    margin: 0;
    font-size: 18px;
    color: var(--el-text-color-primary);
}

.results-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.base-info, .accounts-info {
    background: var(--el-bg-color);
    padding: 16px;
    border-radius: 8px;
}

.base-info h4, .accounts-info h4 {
    margin: 0 0 12px 0;
    font-size: 16px;
    color: var(--el-text-color-primary);
}

.base-info p {
    margin: 8px 0;
    color: var(--el-text-color-regular);
}

.account-list {
    max-height: 300px;
    overflow-y: auto;
}

.account-list p {
    margin: 8px 0;
    color: var(--el-text-color-regular);
    font-family: monospace;
}

.progress-info {
    text-align: center;
}

.progress-info p {
    margin: 12px 0;
    color: var(--el-text-color-regular);
}

@media (max-width: 768px) {
    .content-container {
        flex-direction: column;
    }

    .register-form {
        min-width: unset;
    }

    .register-form :deep(.el-form-item) {
        margin-bottom: 18px;
    }

    .register-form :deep(.el-input),
    .register-form :deep(.el-input-number) {
        width: 100% !important;
    }

    .register-form :deep(.el-button) {
        width: 100% !important;
    }
}
</style> 